import { ESdChangeReason, IFilterItem, IKuikaStyle, InvisibleStylePart, Visibility } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import React from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import PopoverInput from "./popover-input/popover-input";
import FilterPopoverContent from "./styling-effects-filter-popover-content";
import { defaultFilterItem, summarizeFilter } from "./styling-effects-helpers";
import { styleSectionNeedsRender } from "./styling-window-helpers";

/**
 * Faz 4 — Filters + Backdrop Filters panel. One container, two sections.
 *
 * Manual controlled-array pattern (Form.List intentionally avoided — Faz 3's
 * TransformAccordion documented why: Form.List's mount/unmount semantics fight
 * with PopoverInput open-state and per-type dynamic forms). State lives on
 * the parent antd Form via `["effects", "filters"]` and `["effects",
 * "backdropFilters"]` paths; row list re-renders via Form.Item shouldUpdate.
 */
export interface IEffectsFiltersComponentProps {
  form: FormInstance<IKuikaStyle>;
}

type FilterFieldKey = "filters" | "backdropFilters";

interface IEffectsFiltersComponentState {
  placeholder: never[];
}

class EffectsFiltersComponent extends PureSdComponent<
  IEffectsFiltersComponentProps & IWithSDProps,
  IEffectsFiltersComponentState
> {
  constructor(props: IEffectsFiltersComponentProps & IWithSDProps) {
    super(props);
    this.state = { placeholder: [] };
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => styleSectionNeedsRender(changeReason);

  private isEffectsHidden = (): boolean =>
    this.props.sd.getStylesVisibility(InvisibleStylePart.Effects) === Visibility.Hidden;

  private ensureEffectsSection = (fieldsValue: IKuikaStyle): void => {
    const kuikaid: string | undefined = this.props.sd.selectedKuikaIds[0];
    if (!fieldsValue.effects) {
      fieldsValue.effects = { effectsId: null, kuikaid: kuikaid ?? "" };
    }
  };

  private submitForm = (fieldsValue: IKuikaStyle): void => {
    // Do NOT call form.resetFields() here — it unmounts every Form.Item
    // subtree, which resets the local `useState` open-state inside each
    // PopoverInput rendered under shouldUpdate. Popovers close mid-edit
    // as a result. setFieldsValue alone replaces the filters/backdrop
    // arrays; the explicit submit() triggers persistence.
    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();
  };

  private readFilters = (field: FilterFieldKey): IFilterItem[] =>
    (this.props.form.getFieldValue(["effects", field]) as IFilterItem[]) || [];

  private commitFilters = (field: FilterFieldKey, next: IFilterItem[]): void => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    this.ensureEffectsSection(fieldsValue);
    if (fieldsValue.effects) {
      fieldsValue.effects[field] = next;
    }
    this.submitForm(fieldsValue);
  };

  private patchFilterAt = (field: FilterFieldKey, index: number, patch: Partial<IFilterItem>): void => {
    const current: IFilterItem[] = this.readFilters(field);
    const target: IFilterItem | undefined = current[index];
    if (!target) return;
    const next: IFilterItem[] = current.map((it: IFilterItem, i: number): IFilterItem =>
      i === index ? { ...target, ...patch } : it
    );
    this.commitFilters(field, next);
  };

  private replaceFilterAt = (field: FilterFieldKey, index: number, replacement: IFilterItem): void => {
    const current: IFilterItem[] = this.readFilters(field);
    if (!current[index]) return;
    const next: IFilterItem[] = current.map((it: IFilterItem, i: number): IFilterItem =>
      i === index ? replacement : it
    );
    this.commitFilters(field, next);
  };

  private removeFilterAt = (field: FilterFieldKey, index: number): void => {
    const current: IFilterItem[] = this.readFilters(field);
    this.commitFilters(
      field,
      current.filter((_: IFilterItem, i: number): boolean => i !== index)
    );
  };

  private addFilter = (field: FilterFieldKey): void => {
    const current: IFilterItem[] = this.readFilters(field);
    this.commitFilters(field, [...current, defaultFilterItem("blur")]);
  };

  private renderSection = (
    field: FilterFieldKey,
    title: string,
    addAriaLabel: string,
    testIdPrefix: string
  ): React.ReactNode => {
    return (
      <div className={`effects-section__${testIdPrefix}`}>
        <div className="effects-section__multi-header">
          <KuikaSmallText style={{ color: "var(--dark-7)", fontWeight: 500 }}>{title}</KuikaSmallText>
          <div className="effects-section__multi-header-actions">
            <button
              type="button"
              aria-label={addAriaLabel}
              className="effects-section__icon-button"
              onClick={(e: React.MouseEvent): void => {
                e.stopPropagation();
                this.addFilter(field);
              }}
              data-testid={`${testIdPrefix}-add`}
            >
              <KuikaSvg name="plus" width={14} height={14} />
            </button>
          </div>
        </div>
        <Form.Item noStyle shouldUpdate>
          {() => {
            const items: IFilterItem[] = this.readFilters(field);
            if (items.length === 0) {
              return null;
            }
            return (
              <div className="effects-section__multi-body">
                {items.map((item: IFilterItem, index: number) => (
                  <PopoverInput
                    key={item.id}
                    value={summarizeFilter(item)}
                    placeholder="Configure filter"
                    onClear={(): void => this.removeFilterAt(field, index)}
                    popoverContent={
                      <FilterPopoverContent
                        item={item}
                        onPatch={(patch: Partial<IFilterItem>): void => this.patchFilterAt(field, index, patch)}
                        onTypeChange={(next: IFilterItem): void => this.replaceFilterAt(field, index, next)}
                        allowDropShadow={field === "filters"}
                        dataTestIdPrefix={`${testIdPrefix}-${index}`}
                      />
                    }
                    popoverTitle={title}
                    popoverPosition="left"
                    popoverAlign="end"
                    dataTestId={`${testIdPrefix}-${index}`}
                  />
                ))}
              </div>
            );
          }}
        </Form.Item>
      </div>
    );
  };

  render(): React.ReactNode {
    if (this.isEffectsHidden()) {
      return null;
    }
    return (
      <>
        {this.renderSection("filters", "Filters", "Add filter", "effects-filter")}
        {this.renderSection("backdropFilters", "Backdrop Filters", "Add backdrop filter", "effects-backdrop-filter")}
      </>
    );
  }
}

export default withSD<IEffectsFiltersComponentProps>(EffectsFiltersComponent);
